import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2,
  AlertTriangle,
  Phone,
  Mail,
  Clock,
  ExternalLink,
  Plus,
  User,
  FileText,
  Sparkles,
  MessageCircle,
  Handshake,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

const STAGES = [
  { key: "lead_nou",  label: "Lead Nou",       icon: Sparkles,      accent: "border-gold bg-gold-soft/40" },
  { key: "contactat", label: "Contactat",      icon: MessageCircle, accent: "border-amber-300 bg-amber-50/40" },
  { key: "oferta",    label: "Ofertă Trimisă", icon: Handshake,     accent: "border-violet-300 bg-violet-50/40" },
  { key: "finalizat", label: "Finalizat",      icon: CheckCircle2,  accent: "border-emerald-300 bg-emerald-50/40" },
];

const INTERACTION_TYPES = [
  { v: "call",       label: "Apel telefonic",  icon: Phone },
  { v: "email",      label: "Email",           icon: Mail },
  { v: "meeting",    label: "Întâlnire",       icon: User },
  { v: "offer_sent", label: "Ofertă trimisă",  icon: FileText },
  { v: "note",       label: "Notă",            icon: MessageCircle },
];

function timeAgo(hours) {
  if (hours == null) return "—";
  if (hours < 1) return "acum câteva minute";
  if (hours < 24) return `acum ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `acum ${days} ${days === 1 ? "zi" : "zile"}`;
  const weeks = Math.floor(days / 7);
  return `acum ${weeks} săpt.`;
}

export default function Sales() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // client id being viewed
  const [dragOverStage, setDragOverStage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const c of clients) {
      const key = c.lead_status || "lead_nou";
      if (g[key]) g[key].push(c);
    }
    return g;
  }, [clients]);

  const alertsCount = useMemo(
    () => clients.filter((c) => c.lead_status === "lead_nou" && (c.hours_since_contact ?? 0) > 48).length,
    [clients],
  );

  const moveClient = async (clientId, newStatus) => {
    const cur = clients.find((c) => c.id === clientId);
    if (!cur || cur.lead_status === newStatus) return;
    // optimistic update
    setClients((cs) => cs.map((c) => (c.id === clientId ? { ...c, lead_status: newStatus, hours_since_contact: newStatus === "lead_nou" ? c.hours_since_contact : 0 } : c)));
    try {
      await api.patch(`/clients/${clientId}/status`, { lead_status: newStatus });
      const label = STAGES.find((s) => s.key === newStatus)?.label || newStatus;
      toast.success(`Mutat în „${label}"`);
    } catch (e) {
      toast.error("Nu s-a putut actualiza statusul");
      load();
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Panou vânzări</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Sales Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Trage cardurile între coloane pentru a schimba etapa. Lead-urile ne-contactate peste 48h sunt marcate.
          </p>
        </div>
        <div className="flex gap-3">
          <div className={`border rounded-md px-4 py-3 ${alertsCount > 0 ? "border-red-300 bg-red-50" : "border-border bg-white"}`} data-testid="alerts-summary">
            <p className="label-eyebrow flex items-center gap-1">
              <AlertTriangle className={`w-3 h-3 ${alertsCount > 0 ? "text-red-600" : "text-muted-foreground"}`} />
              Alerte
            </p>
            <p className={`font-display font-bold text-2xl mt-1 ${alertsCount > 0 ? "text-red-600" : "text-slate-900"}`}>
              {alertsCount}
            </p>
          </div>
          <div className="border border-border rounded-md px-4 py-3 bg-white">
            <p className="label-eyebrow">Total lead-uri</p>
            <p className="font-display font-bold text-2xl mt-1 text-slate-900">{clients.length}</p>
          </div>
        </div>
      </header>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {STAGES.map((stage) => {
          const items = grouped[stage.key] || [];
          const isTarget = dragOverStage === stage.key;
          return (
            <div
              key={stage.key}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.key); }}
              onDragLeave={() => setDragOverStage((s) => (s === stage.key ? null : s))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStage(null);
                const id = e.dataTransfer.getData("text/plain");
                if (id) moveClient(id, stage.key);
              }}
              className={`rounded-md border ${stage.accent} p-3 flex flex-col min-h-[300px] transition-colors ${isTarget ? "ring-2 ring-[hsl(var(--gold))] ring-offset-2" : ""}`}
              data-testid={`stage-${stage.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <stage.icon className="w-4 h-4 text-slate-700" />
                  <p className="font-display font-bold text-sm">{stage.label}</p>
                </div>
                <span className="text-xs font-mono-crm bg-white border border-border rounded-sm px-2 py-0.5">
                  {items.length}
                </span>
              </div>

              <div className="space-y-2 flex-1">
                {loading ? (
                  <p className="text-xs text-muted-foreground italic p-4 text-center">Se încarcă...</p>
                ) : items.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic p-4 text-center">
                    Trage un card aici sau adaugă un client din pagina „Clienți".
                  </p>
                ) : items.map((c) => (
                  <LeadCard
                    key={c.id}
                    client={c}
                    onOpen={() => setSelected(c.id)}
                    onMove={moveClient}
                    stageKey={stage.key}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <LeadDetailModal
          clientId={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function LeadCard({ client, onOpen, onMove, stageKey }) {
  const overdue = stageKey === "lead_nou" && (client.hours_since_contact ?? 0) > 48;
  const next = STAGES[STAGES.findIndex((s) => s.key === stageKey) + 1];

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", client.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={`group bg-white border rounded-md p-3 cursor-grab active:cursor-grabbing transition-all ${
        overdue ? "border-red-400 shadow-[0_0_0_1px_rgba(220,38,38,0.15)]" : "border-border hover:border-slate-400"
      }`}
      data-testid={`lead-card-${client.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="text-left w-full"
        data-testid={`lead-card-open-${client.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="label-eyebrow">CUI {client.cui}</p>
            <p className="font-display font-bold text-sm mt-1 truncate">{client.denumire}</p>
          </div>
          {overdue && (
            <span
              className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-red-700 bg-red-50 border border-red-200 rounded-sm px-1.5 py-0.5"
              title="Ne-contactat de mai mult de 48h"
              data-testid={`lead-alert-${client.id}`}
            >
              <AlertTriangle className="w-3 h-3" /> 48h+
            </span>
          )}
        </div>

        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          {client.localitate || client.judet ? (
            <p className="flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3 shrink-0" />
              {[client.localitate, client.judet].filter(Boolean).join(", ")}
            </p>
          ) : null}
          {client.telefon && (
            <p className="flex items-center gap-1 truncate">
              <Phone className="w-3 h-3 shrink-0" /> {client.telefon}
            </p>
          )}
          {client.email && (
            <p className="flex items-center gap-1 truncate">
              <Mail className="w-3 h-3 shrink-0" /> {client.email}
            </p>
          )}
        </div>

        <div className="mt-2 pt-2 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground">
          <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : ""}`}>
            <Clock className="w-3 h-3" />
            {timeAgo(client.hours_since_contact)}
          </span>
          <span className="flex items-center gap-2">
            {client.contracts_count > 0 && <span title="Contracte">📄 {client.contracts_count}</span>}
            {client.pv_count > 0 && <span title="PV-uri">✔ {client.pv_count}</span>}
          </span>
        </div>
      </button>

      {next && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMove(client.id, next.key); }}
          className="mt-2 w-full text-xs font-medium border border-border rounded-md py-1.5 opacity-0 group-hover:opacity-100 hover:border-gold hover:text-gold-deep transition-all flex items-center justify-center gap-1"
          data-testid={`lead-next-${client.id}`}
        >
          Mută în „{next.label}" <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function LeadDetailModal({ clientId, onClose, onChanged }) {
  const [client, setClient] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "call", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/interactions`),
      ]);
      setClient(c.data);
      setInteractions(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const submitInteraction = async (e) => {
    e.preventDefault();
    if (!form.note.trim()) {
      toast.error("Adaugă o notă");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/interactions`, form);
      toast.success("Interacțiune înregistrată");
      setForm({ type: form.type, note: "" });
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeStage = async (newStatus) => {
    try {
      await api.patch(`/clients/${clientId}/status`, { lead_status: newStatus });
      toast.success("Etapă actualizată");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {loading ? "Se încarcă..." : client?.denumire}
          </DialogTitle>
          <DialogDescription>
            CUI {client?.cui} {client?.nr_reg_com && `• ${client.nr_reg_com}`}
          </DialogDescription>
        </DialogHeader>

        {!loading && client && (
          <div className="space-y-6">
            {/* Firm details */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem icon={Building2} label="Adresă" value={[client.adresa, client.localitate, client.judet].filter(Boolean).join(", ") || "—"} />
              <InfoItem icon={User} label="Persoană contact" value={client.persoana_contact || "—"} />
              <InfoItem icon={Phone} label="Telefon" value={client.telefon || "—"} />
              <InfoItem icon={Mail} label="Email" value={client.email || "—"} />
            </section>

            {/* Stage picker */}
            <section>
              <Label className="label-eyebrow">Etapă</Label>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                {STAGES.map((s) => {
                  const active = client.lead_status === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => changeStage(s.key)}
                      data-testid={`detail-stage-${s.key}`}
                      className={`border rounded-md py-2 px-3 text-sm font-medium transition-colors ${
                        active
                          ? "border-gold bg-gold-soft text-gold-deep"
                          : "border-border bg-white hover:border-slate-400"
                      }`}
                    >
                      <s.icon className="w-4 h-4 inline-block mr-1" /> {s.label}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/clienti/${clientId}`}
                className="text-sm font-medium border border-border rounded-md px-3 py-2 hover:border-gold hover:text-gold-deep transition-colors flex items-center gap-2"
                data-testid="open-client-page"
              >
                <ExternalLink className="w-4 h-4" /> Deschide fișa completă
              </Link>
            </div>

            {/* Interaction form */}
            <section className="border border-border rounded-md p-4 bg-slate-50/50">
              <p className="label-eyebrow mb-3">Înregistrează o interacțiune</p>
              <form onSubmit={submitInteraction} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="h-10" data-testid="interaction-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INTERACTION_TYPES.map((t) => (
                        <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Notă scurtă: subiectul discuției, rezultat, next step..."
                    rows={2}
                    className="text-sm"
                    data-testid="interaction-note"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="btn-gold hover:btn-gold"
                  data-testid="save-interaction"
                >
                  <Plus className="w-4 h-4 mr-1" /> {saving ? "Se salvează..." : "Adaugă"}
                </Button>
              </form>
            </section>

            {/* Interaction timeline */}
            <section>
              <p className="label-eyebrow mb-3">Istoric interacțiuni ({interactions.length})</p>
              {interactions.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Nicio interacțiune înregistrată încă.</p>
              ) : (
                <ol className="space-y-3">
                  {interactions.map((it) => {
                    const typeMeta = INTERACTION_TYPES.find((t) => t.v === it.type) || { icon: MessageCircle, label: it.type };
                    const Icon = typeMeta.icon;
                    return (
                      <li key={it.id} className="flex gap-3">
                        <div className="w-8 h-8 rounded-md border border-border bg-white flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-slate-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-sm font-medium">{typeMeta.label}</p>
                            <p className="text-xs text-muted-foreground shrink-0">
                              {new Date(it.created_at).toLocaleString("ro-RO")}
                            </p>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-line">{it.note}</p>
                          {it.created_by_name && (
                            <p className="text-xs text-muted-foreground mt-0.5">de {it.created_by_name}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 border border-border rounded-md flex items-center justify-center shrink-0 bg-slate-50">
        <Icon className="w-4 h-4 text-slate-700" />
      </div>
      <div className="min-w-0">
        <p className="label-eyebrow">{label}</p>
        <p className="text-sm mt-1 break-words">{value}</p>
      </div>
    </div>
  );
}
